/* 360 product spin.
 *
 * Each product hero is the real turntable, 36 registered cutouts, drag to spin.
 * Adapted from marketing2.0/octogun-rotator-mock/clear-360.html, which is the
 * version that has already been through this project's two failure modes:
 *
 *   1. Frames are painted to a canvas, never swapped into an <img>. Setting
 *      img.src per frame blanks the element while each frame decodes, which
 *      drops the tube out of the page mid spin.
 *
 *   2. requestAnimationFrame does not fire at all while the page is hidden, and
 *      in a backgrounded or embedded pane it can stay starved indefinitely,
 *      leaving the tube frozen on one frame with no error to show for it. So a
 *      timer takes over whenever rAF has gone quiet, and the step is clamped so
 *      the first frame after a long pause advances once rather than lurching
 *      through a whole rotation.
 *
 * Progressive enhancement: the markup ships the static front-face shot as a
 * plain <img>. This only takes over once the frames are decoded, so with JS off,
 * or if a frame 404s, the page still shows the product.
 */
(function () {
  'use strict';

  var nbnA = window.nbnAsset || function (p) { return p; };
  var boxes = document.querySelectorAll('[data-spin]');
  if (!boxes.length || !document.createElement('canvas').getContext) return;

  var REDUCED = matchMedia('(prefers-reduced-motion:reduce)').matches;

  function Tube(el) {
    var self = this;
    this.el = el;
    this.dir = el.dataset.spin;                 // frame folder, with trailing /
    this.still = el.querySelector('img');

    /* 9 Sep 2026, mobile. A phone gets the small frame set when the markup
       offers one: data-spin-sm names a folder of 768px-tall frames, about a
       third of the bytes of the full set, and data-meta-sm carries that set's
       own w/h/base/bodyw/cx (the poster-side numbers, canvas/stand/cyl, are
       the same because the poster is the same picture). Decided once, at boot,
       on the theme's own 768px breakpoint; a phone rotated to landscape keeps
       the set it loaded. */
    var SMALL = matchMedia('(max-width: 768px)').matches;
    if (SMALL && el.dataset.spinSm) this.dir = el.dataset.spinSm;

    // Prefer the inlined geometry. Fetching a local meta.json is a cross-origin
    // request that file:// blocks, so a page opened by double-click would lose
    // the turntable for no visible reason. The fetch stays as a fallback.
    var inline = (SMALL && el.getAttribute('data-meta-sm')) || el.getAttribute('data-meta');
    if (inline) {
      try { this.start(JSON.parse(inline)); return; } catch (e) { /* fall through */ }
    }
    fetch(this.dir + 'meta.json')
      .then(function (r) { return r.json(); })
      .then(function (m) { self.start(m); })
      .catch(function () { /* keep the static shot */ });
  }

  Tube.prototype.start = function (m) {
    var self = this;
    this.m = m;
    this.k = m.front;

    var loaded = 0;
    this.imgs = [];
    var pending = m.n;
    for (var i = 0; i < m.n; i++) {
      (function (i) {
        var im = new Image();
        im.onload = im.onerror = function () {
          if (im.naturalWidth) loaded++;
          if (--pending === 0) self.ready(loaded);
        };
        im.src = nbnA(self.dir + 'f' + String(i).padStart(2, '0') + '.webp');
        self.imgs[i] = im;
      })(i);
    }
  };

  Tube.prototype.ready = function (loaded) {
    // A partial set would stutter. Only take over if every frame decoded.
    if (loaded < this.m.n) return;

    this.cv = document.createElement('canvas');
    this.ctx = this.cv.getContext('2d');
    this.el.appendChild(this.cv);
    this.el.classList.add('is-spinnable');
    if (this.still) this.still.style.visibility = 'hidden';

    var self = this;
    this.layout();
    if (window.ResizeObserver) new ResizeObserver(function () { self.layout(); }).observe(this.el);
    else addEventListener('resize', function () { self.layout(); });

    this.bindDrag();
    /* data-spin-auto="off" keeps the drag and the arrow keys but skips the idle
       rotation, and rests the tube on its front face. One turntable spinning on
       its own is an invitation; five of them side by side on the samples page is
       just noise, and every one of the five settles on a different frame on the
       way down. */
    if (this.el.dataset.spinAuto === 'off') { this.k = this.m.front; this.draw(); }
    else this.bindAuto();
  };

  /* The box is sized in the flow by the static shot inside it, so the canvas has
     to reproduce that shot's geometry exactly rather than fitting itself to the
     box. Scaling the frame to the box height instead would draw the tube at a
     different size and off the shot's standing line, and the product would
     visibly jump the moment the frames finished decoding.

     meta.canvas / meta.stand / meta.cyl describe the static hero PNG. The box
     displays that PNG at height h, so everything in it scales by h/canvas. */
  Tube.prototype.layout = function () {
    if (!this.cv) return;
    var h = this.el.clientHeight;
    if (!h) return;
    var m = this.m;
    var disp = h / m.canvas;                 // PNG pixels to CSS pixels
    var cyl = m.cyl * disp;                  // cylinder width on screen
    var scale = cyl / m.bodyw;               // source frame to CSS pixels
    var dpr = Math.min(devicePixelRatio || 1, 2);
    var cw = m.w * scale, ch = m.h * scale;

    this.cv.width = Math.round(cw * dpr);
    this.cv.height = Math.round(ch * dpr);
    this.cv.style.width = cw + 'px';
    this.cv.style.height = ch + 'px';
    this.cv.style.left = '50%';
    this.cv.style.marginLeft = (-m.cx * scale) + 'px';
    // Land the frame's own standing line on the shot's standing line.
    this.cv.style.top = (m.stand * disp - m.base * scale) + 'px';
    this.cv.style.bottom = 'auto';
    this.el.style.setProperty('--cyl', cyl + 'px');
    this.el.style.setProperty('--stand', (m.stand * disp) + 'px');
    this.draw();
  };

  Tube.prototype.draw = function () {
    if (!this.cv || !this.cv.width) return;
    var im = this.imgs[this.k];
    if (!im || !im.naturalWidth) return;
    this.ctx.clearRect(0, 0, this.cv.width, this.cv.height);
    this.ctx.drawImage(im, 0, 0, this.cv.width, this.cv.height);
  };

  Tube.prototype.setFrame = function (f) {
    var n = this.m.n;
    var k = ((Math.round(f) % n) + n) % n;
    if (k === this.k) return;
    this.k = k;
    this.draw();
  };

  Tube.prototype.bindDrag = function () {
    var self = this, id = null, x0 = 0, f0 = 0;
    var el = this.el;
    el.setAttribute('role', 'img');
    el.tabIndex = 0;

    el.addEventListener('pointerdown', function (e) {
      id = e.pointerId; x0 = e.clientX; f0 = self.k;
      el.setPointerCapture(id);
      el.classList.add('is-grabbing');
      self.auto = false;
      el.classList.add('has-spun');
    });
    el.addEventListener('pointermove', function (e) {
      if (e.pointerId !== id) return;
      e.preventDefault();
      self.setFrame(f0 - (e.clientX - x0) / (el.clientWidth / 30));
    });
    function up(e) {
      if (e.pointerId === id) { id = null; el.classList.remove('is-grabbing'); }
    }
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);

    // Keyboard: arrows step the rotation for anyone not using a pointer.
    el.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      self.auto = false;
      el.classList.add('has-spun');
      self.setFrame(self.k + (e.key === 'ArrowRight' ? -1 : 1));
    });
  };

  Tube.prototype.bindAuto = function () {
    var self = this;
    var RPS = 4.4;                    // frames of rotation per second
    var SPIN_MS = 9000;               // free rotation, then settle
    this.auto = !REDUCED;
    var t = this.k, last = 0, started = 0, target = null;

    /* The idle spin does not simply stop. Stopping wherever the clock happens to
       land leaves the cartridge showing its back panel, which is dense legal
       text, so the product would settle on its worst face about half the time.
       Instead it eases to the nearest front-face alignment and rests there,
       label to camera, which is also the frame the static shot uses. */
    function step(now) {
      if (!self.auto) { last = 0; return; }
      if (!started) started = now;
      if (last) {
        var dt = Math.min(now - last, 200) / 1000;
        if (target === null && now - started > SPIN_MS) {
          var n = self.m.n;
          target = Math.floor((t - self.m.front) / n) * n + self.m.front;
          if (t - target > n / 2) target -= n;   // always ease the short way
        }
        if (target === null) {
          t -= dt * RPS;
        } else {
          t += (target - t) * Math.min(1, dt * 4.5);
          if (Math.abs(target - t) < 0.04) { t = target; self.auto = false; }
        }
        self.setFrame(t);
      }
      last = now;
    }
    (function raf() { requestAnimationFrame(function (now) { step(now); raf(); }); })();
    setInterval(function () {         // only acts if rAF has gone quiet
      var now = performance.now();
      if (now - last > 220) step(now);
    }, 120);
  };

  /* DO NOT FETCH 36 FRAMES FOR A TURNTABLE THE READER HAS NOT REACHED.
   *
   * Measured on samples.html at 375pt: the four picker turntables sit 4.6 and
   * 5.3 viewports down, and between them they pulled 9.7 MB before anything on
   * the first screen had settled. Each set is 1.8 to 3.1 MB and each renders
   * 154 CSS px wide.
   *
   * So a tube now waits until it is one viewport away, and only then loads. The
   * markup already ships the static front-face shot, so an unreached turntable
   * still shows its product; nothing is hidden behind this, only deferred.
   * rootMargin is a whole viewport so the frames are usually in by the time the
   * tube is actually on screen.
   *
   * Product pages that carry a single turntable in the hero are unaffected in
   * practice: their tube is already inside the root margin at load, so it starts
   * immediately, exactly as before.
   */
  function boot(el) { new Tube(el); }

  /* CONFIRM WITH A FRESH RECT BEFORE BOOTING.
   *
   * An observer set up at parse time delivers its first records against the
   * FIRST layout, and on samples.html the hero stage has not taken its height
   * by then, so the whole page is still short. Two of the four turntables were
   * reported as intersecting while they in fact sat 3762px down a 9603px page,
   * and they loaded their full sets anyway. isIntersecting was not wrong, it was
   * answering about a layout that no longer exists by the time it is read.
   *
   * So the callback re-measures and only boots what is genuinely near the
   * viewport, and keeps observing anything that is not. */
  var NEAR = 1;                       // viewports of lead-in

  function nearViewport(el) {
    var r = el.getBoundingClientRect();
    var pad = window.innerHeight * NEAR;
    return r.top < window.innerHeight + pad && r.bottom > -pad;
  }

  if (!window.IntersectionObserver) {
    Array.prototype.forEach.call(boxes, boot);
  } else {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        if (!nearViewport(e.target)) return;    // stale first layout, keep waiting
        io.unobserve(e.target);
        boot(e.target);
      });
    }, { rootMargin: '100% 0px' });
    Array.prototype.forEach.call(boxes, function (el) { io.observe(el); });
  }
})();
